'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useStyles } from '@/context/StyleContext';
import { cn } from '@/lib/utils';
import { editorDrag } from '@/lib/editorDrag';


interface EditableContentProps {
  id: string;
  className?: string;
  showEditIcon?: boolean;
  as?: React.ElementType;
}


export function EditableContent({ id, className, showEditIcon, as: Component = 'span' }: EditableContentProps) {
  const { t, isAdmin, language } = useLanguage();
  const { getStyle, openStyleEditor, closeStyleEditor, editingStyleId } = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState('');
  const contentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setContent(t(id));
  }, [id, t]);

  const handleDoubleClick = () => {
    if (isAdmin) {
      // Text editing takes over: the overlay switches to 'text' mode (rect only)
      openStyleEditor(id, contentRef.current, 'text');
      setIsEditing(true);
      // Use setTimeout to ensure the element is focusable
      setTimeout(() => {
        contentRef.current?.focus();
      }, 0);
    }
  };

  const handleBlur = async () => {
    setIsEditing(false);
    closeStyleEditor();
    const newText = contentRef.current?.innerText || '';
    
    if (newText !== content) {
      // Dispatch saving status
      window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'saving' } }));

      try {
        const response = await fetch('/api/save-content/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            language,
            id,
            text: newText,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Save failed:', errorData);
          throw new Error(errorData.error || 'Failed to save');
        } else {
          setContent(newText);
          window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'saved' } }));
        }
      } catch (error) {
        console.error('Error saving content:', error);
        // Revert on error
        setContent(t(id));
        window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'error' } }));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      contentRef.current?.blur();
    }
    if (e.key === 'Escape') {
      if (contentRef.current) contentRef.current.innerText = content;
      setIsEditing(false);
      closeStyleEditor();
    }
  };

  return (
    <Component
      ref={contentRef}
      contentEditable={isEditing}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => {
        // Never re-anchor the editor while a handle drag is in progress:
        // crossing another field mid-stretch used to hijack the overlay.
        if (isAdmin && !isEditing && !editorDrag.active && contentRef.current) {
          openStyleEditor(id, contentRef.current, 'style');
        }
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      suppressContentEditableWarning
      style={getStyle(id)}
      className={cn(
        className,
        "select-text",
        isAdmin && "transition-all cursor-edit",
        // The editor border is the element's own outline: browsers paint it as a
        // connected stepped path around inline line fragments (same shape as the
        // contentEditable focus ring), so it always hugs the text exactly.
        isAdmin && editingStyleId === id && "outline outline-2 outline-primary",
        isEditing && "bg-primary/5 shadow-inner min-w-[20px]"
      )}
      title={isAdmin ? "Double click to edit" : undefined}
    >
      {content}
    </Component>
  );
}
